import React, { Children, cloneElement, Component } from 'react';

import { StepsAPI } from '@philips-emr/tws-components-api';
import { Expression, Key, StepStage } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, element, eventListener, expression, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { createAbort, createStep, editStep, getStepIdentifiers, isDefined, isEqualShallow, isFunction, isMobile, noop, resetStep, submitStep, trigger, updateStepStages  } from '@philips-emr/tws-utils';

import './steps.scss';

import { Button } from '..';

const acceptedTypes = [
  ComponentType.FORM,
];

@AwesomeComponent({ type: ComponentType.FORM, api: StepsAPI })
class Steps extends Component {
  static propTypes = {
    children: oneOfType([element, arrayOf(element)]),
    confirmExpression: expression,
    hiddenSteps: arrayOf(string).notEnumerable(),
    nextExpression: expression,
    onAfterNavigate: eventListener,
    onChange: eventListener,
    onDynamicContentRender: eventListener,
    onNavigate: eventListener,
    onSubmit: eventListener,
    uuid: string,
  };

  static defaultProps = {
    confirmExpression: Expression.CONFIRM,
    hiddenSteps: [],
    nextExpression: Expression.NEXT,
  };

  state = {
    currentStep: '',
    steps: {},
    previousStep: '',
  };

  componentDidMount() {
    const steps = this.getSteps();

    this.setInitialStepsState(steps);
  }

  componentDidUpdate(prevProps) {
    const { hiddenSteps = [] } = this.props;
    const { hiddenSteps: prevHiddenSteps } = prevProps;

    if (!isEqualShallow(hiddenSteps, prevHiddenSteps)) {
      this.setInitialStepsState(this.getSteps());
    }
  }

  getStepsMetadata() {
    const { children, hiddenSteps } = this.props;

    return Children.toArray(children)
      .filter((child) => isDefined(child.props.identifier) ? !hiddenSteps.includes(child.props.identifier) : true)
      .map((child, index) => ({ ...child, props: { ...child.props, index } }));
  }

  getSteps() {
    const steps = this.getStepsMetadata();

    return steps.map((element, index) => {
      const { identifier } = element.props || {};

      const properties = createStep(resetStep({}), {
        enabled: false,
        identifier,
        last: index === steps.length - 1,
        onEdit: this.onStepEdit,
        onPrevious: this.onPreviousStep,
        onSubmit: this.onStepSubmit,
        stage: StepStage.NONE,
      });

      return { element, properties };
    });
  }

  getStepProperties(step) {
    const { steps } = this.state;

    return {
      ...step,
      ...steps[step.identifier],
    };
  }

  getStepResult(stepIdentifier) {
    const { onDynamicContentRender } = this.props;

    let renderFn = null;

    const render = (fn) => (renderFn = fn);

    trigger(onDynamicContentRender, { type: 'step-result', render, entry: stepIdentifier })();

    if (renderFn && isFunction(renderFn)) {
      return renderFn();
    }

    return null;
  }

  getCurrentStep() {
    const { currentStep, steps } = this.state;

    return steps[currentStep];
  }

  getSliderPercentage = () => {
    const { currentStep, steps } = this.state;

    const index = getStepIdentifiers(steps).findIndex(
      (identifier) => identifier === currentStep
    );

    return index >= 0 ? (index * 100) * -1 : 0;
  };

  setInitialStepsState(steps = []) {
    const stepsState = steps.reduce((accumulator, step) => {
      const { properties } = step;

      return { ...accumulator, [properties.identifier]: { ...properties } };
    }, {});

    const firstIdentifier = getStepIdentifiers(stepsState)[0];

    stepsState[firstIdentifier] = {
      ...stepsState[firstIdentifier],
      collapsed: false,
      enabled: true,
      stage: StepStage.CURRENT,
    };

    this.setState({
      currentStep: firstIdentifier,
      steps: updateStepStages(firstIdentifier, stepsState),
    });
  }

  onStepEdit = (stepIdentifier) => {
    const { steps, currentStep } = this.state;

    const result = editStep(currentStep, stepIdentifier, steps);

    this.setState({
      currentStep: result.identifier,
      steps: updateStepStages(result.identifier, result.steps),
      previousStep: currentStep,
    }, this.fireOnNavigate);
  };

  fireOnNavigate = () => {
    const { onAfterNavigate, onNavigate } = this.props;
    const { previousStep, currentStep } = this.state;

    if (isFunction(onNavigate)) {
      onNavigate({ abort: noop, from: previousStep, to: currentStep });
    }

    if (isFunction(onAfterNavigate)) {
      onAfterNavigate({ abort: noop, from: previousStep, to: currentStep });
    }
  };

  onKeyDown = (event)  => {
    if (event.key !== Key.ENTER) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.isStepDone()) {
      this.onNext();
    }
  }

  onStepSubmit = (stepIdentifier) => {
    const { onAfterNavigate, onNavigate } = this.props;
    const { steps, currentStep } = this.state;

    const abort = createAbort();
    const result = submitStep(stepIdentifier, steps, this.getStepResult.bind(this));

    if (isFunction(onNavigate)) {
      onNavigate({ abort, from: currentStep, to: result.identifier });
    }

    abort.promise.then((aborted) => {
      if (aborted) {
        return;
      }

      this.setState({
        currentStep: result.identifier,
        previousStep: currentStep,
        steps: updateStepStages(result.identifier, result.steps),
      }, () => {
        if (isFunction(onAfterNavigate)) {
          onAfterNavigate({ abort: noop, from: currentStep, to: result.identifier });
        }
      });
    });
  };

  onPreviousStep = (stepIdentifier) => {
    const { steps, currentStep } = this.state;
    const identifiers = getStepIdentifiers(steps);

    const currentIndex = identifiers.findIndex(identifier => identifier === stepIdentifier);

    if (currentIndex <= 0) {
      return;
    }

    const editIdentifier = identifiers[currentIndex - 1];
    const result = editStep(stepIdentifier, editIdentifier, steps);

    this.setState({
      currentStep: result.identifier,
      steps: updateStepStages(result.identifier, result.steps),
      previousStep: currentStep,
    }, this.fireOnNavigate);
  };

  onNext = () => {
    this.onStepSubmit(this.state.currentStep);
  };

  isLastStep() {
    const step = this.getCurrentStep();

    return step ? step.last : false;
  }

  isStepDone() {
    const step = this.getCurrentStep();

    return step ? step.done : false;
  }

  renderNextButton() {
    if (!isMobile() || this.isLastStep()) {
      return null;
    }

    return (
      <Button
        enabled={this.isStepDone()}
        className="c-steps__next u-hide@gxsm"
        onClick={this.onNext}
        value={this.props.nextExpression}
      />
    );
  }

  renderConfirmButton() {
    if (!this.isLastStep()) {
      return null;
    }

    return (
      <Button
        enabled={this.isStepDone()}
        className="c-steps__confirm"
        onClick={trigger(this.props.onSubmit, {})}
        value={this.props.confirmExpression}
      />
    );
  }

  render() {
    const { uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    const sliderStyle = {
      transform: `translateX(${this.getSliderPercentage()}%)`,
    };

    return (
      <Container acceptedTypes={acceptedTypes} className="c-steps" uuid={uuid}>
        <div className="c-steps__content">
          <div
            className="c-steps__slider"
            style={isMobile() ? sliderStyle : {}}
            onKeyDown={this.onKeyDown}
          >
            {this.getSteps().map((step, index) => {
              return cloneElement(step.element, {
                ...this.getStepProperties(step.properties),
                index,
              });
            })}
          </div>
        </div>
        <div className="c-steps__actions">
          {this.renderNextButton()}
          {this.renderConfirmButton()}
        </div>
      </Container>
    );
  }
}

export default Steps;
